import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { SERVICES, SITE } from "../lib/site";

const Icon = ({ name, className }) => {
  const I = Icons[name] || Icons.Wrench;
  return <I className={className} />;
};

const GROVE = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/fabzdy7b_379E0A4F-9557-483E-91F7-55A4134B3FAF-800x600.jpeg";
const MANITEX = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/08j7l5sd_IMG_4360.jpeg";

const SERVICE_IMG = {
  "crane-rental-operator": GROVE,
  "hydraulic-mobile-crane": MANITEX,
  "hvac-placement": "https://images.pexels.com/photos/19016904/pexels-photo-19016904.jpeg",
  "steel-erection": "https://images.unsplash.com/photo-1493476523860-a6de6ce1b0c3",
  "machinery-moving": MANITEX,
  "truss-lifts": GROVE,
  "emergency-lifting": MANITEX,
};

export default function Services() {
  return (
    <>
      <SEO
        title="Crane Services — Central Florida"
        description="Mobile crane services in Central Florida: crane rental with operator, hydraulic mobile crane, HVAC equipment placement, steel erection, machinery moving, truss lifts and 24/7 emergency lifting."
      />

      <section className="border-b border-[var(--ac-border)] bg-black">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-28">
          <div className="overline mb-4">Services</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            What we lift — and how we lift it.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg leading-relaxed">
            Seven core service lines, all run from our two-crane fleet out of DeLand, FL. Every
            scope arrives pre-planned, fully insured and operator-certified.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 space-y-16 md:space-y-24">
          {SERVICES.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`service-section-${s.slug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 scroll-mt-28"
              >
                <div className={`md:col-span-7 ${reverse ? "md:order-2" : ""}`}>
                  <div className="aspect-[16/10] overflow-hidden border border-[var(--ac-border)] bg-black">
                    <img src={SERVICE_IMG[s.slug]} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className={`md:col-span-5 flex flex-col justify-center ${reverse ? "md:order-1" : ""}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 border border-[var(--ac-border)] flex items-center justify-center bg-[var(--ac-bg-2)]">
                      <Icon name={s.icon} className="w-5 h-5 text-[var(--ac-red)]" />
                    </div>
                    <span className="font-mono-data text-xs text-zinc-500">
                      {String(i + 1).padStart(2, "0")} / SERVICE
                    </span>
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`service-title-link-${s.slug}`}
                      className="hover:text-[var(--ac-red)] transition-colors"
                    >
                      {s.title}
                    </Link>
                  </h2>
                  <p className="mt-5 text-zinc-300 leading-relaxed">{s.description}</p>
                  <p className="mt-3 text-zinc-500 italic text-sm">{s.short}</p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/services/${s.slug}`}
                      className="btn-primary"
                      data-testid={`service-learn-more-${s.slug}`}
                    >
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/contact" className="btn-ghost" data-testid={`service-cta-${s.slug}`}>
                      Get a Quote
                    </Link>
                    <a href={`tel:${SITE.phoneE164}`} className="btn-ghost">
                      Call {SITE.phone}
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}
